import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useApp } from "../../hooks/useApp";
import { getErrorMessageFromError } from "../../components/ErrorMessage";
import * as GeneratorService from "../../services/proposal-generator/GeneratorService";
import { logProposalGenerated } from "../../services/clientActivityLog/ClientActivityLogService";
import { PricingDto, StepDataDto } from "../../model/proposal-generator/ProposalDto";

const ACTIVE_JOB_KEY = "genia_active_job";
const JOB_TTL_MS = 2 * 60 * 60 * 1000; // 2 horas

const GeneratorDataContext = createContext();

const initialStepData = {
    step1: { ...StepDataDto.step1 },
    step2: { ...StepDataDto.step2 },
    step3: { ...StepDataDto.step3 },
    step4: { ...StepDataDto.step4 },
    step5: { ...StepDataDto.step5 },
    step6: { ...StepDataDto.step6 },
    step7: { ...StepDataDto.step7 },
    step8: { ...StepDataDto.step8 },
    step9: { ...StepDataDto.step9 },
    step10: { ...StepDataDto.step10 },
    step11: { ...StepDataDto.step11 },
    step12: { ...StepDataDto.step12 },
    step13: { ...StepDataDto.step13 },
};

const STEP_ID_BY_NUMBER = {
    2: "cover",
    3: "introduction",
    4: "business_partners",
    5: "context",
    6: "general_objective",
    7: "specific_objectives",
    8: "scope",
    9: "architecture",
    10: "solution",
    11: "team",
    12: "timeline",
    13: "investment",
};

const GeneratorDataContextProvider = ({ children }) => {
    // Estado del paso actual
    const [currentStep, setCurrentStep] = useState(1);
    // Pasos que el usuario ha visitado explícitamente (para marcar en verde solo los visitados)
    const [visitedSteps, setVisitedSteps] = useState(new Set([1]));

    // Datos de cada paso
    const [stepData, setStepData] = useState(initialStepData);

    // Contenido generado
    const [generatedContent, setGeneratedContent] = useState({
        markdown: null,
        html: null,
        sections: [],
    });

    // Propuesta RAW
    const [rawProposalText, setRawProposalText] = useState("");

    // Estado de generación
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationCompleted, setGenerationCompleted] = useState(false);

    // Pricing calculado
    const [pricing, setPricing] = useState(new PricingDto());

    // Preview HTML
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewHtmlByStep, setPreviewHtmlByStep] = useState({
        cover: "",
        introduction: "",
        business_partners: "",
    });

    // ID de la propuesta en BD (null = nueva, no guardada)
    const [proposalId, setProposalId] = useState(null);

    const { authInfo, setLoading, setErrorMsg, setSuccessMsg } = useApp();

    // Actualizar datos de un paso específico
    const updateStepData = useCallback((step, data) => {
        setStepData((prev) => ({
            ...prev,
            [`step${step}`]: {
                ...prev[`step${step}`],
                ...data,
            },
        }));
    }, []);

    // Generar propuesta RAW desde la transcripción
    const generateRawProposal = useCallback(async () => {
        try {
            setIsGenerating(true);
            setLoading(true);

            const requestBody = {
                clientName: stepData.step1?.clientName || "",
                clientCompany: stepData.step1?.clientCompany || "",
                projectName: stepData.step1?.clientCompany || stepData.step1?.clientName || "Propuesta Comercial",
                country: stepData.step1?.country || "",
                projectType: stepData.step1?.projectType || "empresarial",
                transcription: stepData.step1?.transcription || "",
                customPrompt: stepData.step1?.customPrompt || null,
            };

            const response = await GeneratorService.generateRawProposal(requestBody);
            if (response.success) {
                setRawProposalText(response.proposal_text || "");
                setSuccessMsg("Propuesta en texto plano generada exitosamente");
            } else {
                setErrorMsg(response.message || "Error al generar propuesta en texto plano");
            }

            setIsGenerating(false);
            setLoading(false);
            return response;
        } catch (error) {
            setIsGenerating(false);
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al generar propuesta en texto plano");
            throw error;
        }
    }, [stepData, setLoading, setErrorMsg, setSuccessMsg]);

    // Ir al siguiente paso (marca el paso actual como visitado)
    const nextStep = useCallback(() => {
        setVisitedSteps((prev) => new Set([...prev, currentStep]));
        setCurrentStep((prev) => Math.min(prev + 1, 13));
    }, [currentStep]);

    // Ir al paso anterior
    const prevStep = useCallback(() => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    }, []);

    // Ir a un paso específico (marca el paso actual como visitado)
    const goToStep = useCallback((step) => {
        if (step >= 1 && step <= 13) {
            setVisitedSteps((prev) => new Set([...prev, currentStep]));
            setCurrentStep(step);
        }
    }, [currentStep]);

    // Generar contenido del paso actual con IA
    const generateStepContent = useCallback(async (step) => {
        try {
            setIsGenerating(true);
            setLoading(true);
            setGenerationProgress(0);

            const stepId = STEP_ID_BY_NUMBER[step];
            if (!stepId) {
                throw new Error("Paso no soportado para generación con IA");
            }
            if (!rawProposalText) {
                const rawResponse = await GeneratorService.generateRawProposal({
                    clientName: stepData.step1?.clientName || "",
                    clientCompany: stepData.step1?.clientCompany || "",
                    projectName: stepData.step1?.clientCompany || stepData.step1?.clientName || "Propuesta Comercial",
                    country: stepData.step1?.country || "",
                    projectType: stepData.step1?.projectType || "empresarial",
                    transcription: stepData.step1?.transcription || "",
                    customPrompt: stepData.step1?.customPrompt || null,
                });
                if (!rawResponse.success) {
                    throw new Error(rawResponse.message || "No se pudo generar la propuesta en texto plano");
                }
                setRawProposalText(rawResponse.proposal_text || "");
            }

            const previousSteps = {};
            for (let i = 1; i < step; i++) {
                const stepKey = `step${i}`;
                if (stepData[stepKey]) {
                    const previousStepId = STEP_ID_BY_NUMBER[i];
                    if (previousStepId) {
                        previousSteps[previousStepId] = { ...stepData[stepKey] };
                    }
                }
            }

            const response = await GeneratorService.generateStep(stepId, {
                step1: { ...stepData.step1 },
                rawProposalText,
                previousSteps,
            });

            if (response.generatedContent) {
                updateStepData(step, response.generatedContent);
            }

            // Si regeneramos Propuesta Solución (step 10), también regenerar Inversión (step 13)
            // para mantener consistencia de montos en ambos pasos
            if (step === 10 && response.generatedContent) {
                setGenerationProgress(50);
                const previousStepsFor13 = {};
                for (let i = 1; i < 13; i++) {
                    const prevStepId = STEP_ID_BY_NUMBER[i];
                    if (!prevStepId) continue;
                    // Usar datos frescos de step 10 (aún no están en stepData por ser setState async)
                    previousStepsFor13[prevStepId] = i === 10
                        ? { ...response.generatedContent }
                        : { ...stepData[`step${i}`] };
                }
                const investmentResponse = await GeneratorService.generateStep("investment", {
                    step1: { ...stepData.step1 },
                    rawProposalText,
                    previousSteps: previousStepsFor13,
                });
                if (investmentResponse.generatedContent) {
                    updateStepData(13, investmentResponse.generatedContent);
                }
            }

            setGenerationProgress(100);
            setSuccessMsg(
                step === 10
                    ? "Propuesta Solución e Inversión regeneradas exitosamente"
                    : "Contenido generado exitosamente"
            );
            setIsGenerating(false);
            setLoading(false);

            return response;
        } catch (error) {
            setIsGenerating(false);
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al generar contenido");
            throw error;
        }
    }, [stepData, rawProposalText, updateStepData, setLoading, setErrorMsg, setSuccessMsg]);

    // Limpiar localStorage de jobs antiguos al montar
    useEffect(() => {
        localStorage.removeItem(ACTIVE_JOB_KEY);
    }, []);

    const generateFullProposal = useCallback(async () => {
        try {
            setIsGenerating(true);
            setGenerationProgress(0);

            // 1. Crear registro en BD
            const dbProposal = await GeneratorService.saveProposal({
                clientId: stepData.step1?.clientId || null,
                clientName: stepData.step1?.clientName || "",
                clientCompany: stepData.step1?.clientCompany || "",
                projectName: stepData.step1?.clientCompany || stepData.step1?.clientName || "Propuesta",
                country: stepData.step1?.country || "",
                status: "GENERATING",
                progress: 0,
                createdBy: authInfo?.id,
            });
            setProposalId(dbProposal.id);

            // 2. Iniciar job en GENIA
            const { jobId } = await GeneratorService.startProposalGeneration({
                clientName: stepData.step1?.clientName || "",
                clientCompany: stepData.step1?.clientCompany || "",
                country: stepData.step1?.country || "",
                projectType: stepData.step1?.projectType || "empresarial",
                transcription: stepData.step1?.transcription || "",
                customPrompt: stepData.step1?.customPrompt || null,
            });

            // 3. Actualizar BD con jobId
            await GeneratorService.updateProposal(dbProposal.id, { jobId });

            // 4. Registrar en Activity Log del cliente (si tiene clientId)
            if (stepData.step1?.clientId) {
                const userName = authInfo?.name || authInfo?.username || "Usuario";
                const proposalName = stepData.step1?.clientCompany || stepData.step1?.clientName || "Propuesta";
                logProposalGenerated(stepData.step1.clientId, authInfo?.id, userName, proposalName)
                    .catch((err) => console.error("[GENIA] Error logging activity:", err));
            }

            setIsGenerating(false);

            // Retornar info para que el UI muestre mensaje y redirija
            return { proposalId: dbProposal.id, jobId };
        } catch (error) {
            console.error("[GENIA] generateFullProposal error:", error);
            setIsGenerating(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al generar la propuesta");
            throw error;
        }
    }, [stepData, authInfo, setErrorMsg]);

    // Regenerar una sección específica
    const regenerateSection = useCallback(async (sectionType, customPrompt = "") => {
        try {
            setIsGenerating(true);
            setLoading(true);

            const response = await GeneratorService.regenerateSection(sectionType, {
                stepData,
                customPrompt,
            });

            // Actualizar la sección regenerada
            setGeneratedContent((prev) => ({
                ...prev,
                sections: prev.sections.map((section) =>
                    section.type === sectionType
                        ? { ...section, ...response.section }
                        : section
                ),
            }));

            setSuccessMsg("Sección regenerada exitosamente");
            setIsGenerating(false);
            setLoading(false);

            return response;
        } catch (error) {
            setIsGenerating(false);
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al regenerar sección");
            throw error;
        }
    }, [stepData, setLoading, setErrorMsg, setSuccessMsg]);

    // Calcular precios
    const calculatePricing = useCallback(async (pricingData) => {
        try {
            setLoading(true);

            const response = await GeneratorService.calculatePricing(pricingData);

            const newPricing = new PricingDto(
                response.currency,
                response.hourlyRate,
                response.estimatedHours,
                response.subtotal,
                response.taxPercentage,
                response.taxAmount,
                response.total,
                response.paymentTerms
            );

            setPricing(newPricing);
            updateStepData(13, { pricing: newPricing });

            setLoading(false);
            return newPricing;
        } catch (error) {
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al calcular precios");
            throw error;
        }
    }, [updateStepData, setLoading, setErrorMsg]);


    const renderPreview = useCallback(async () => {
        try {
            setLoading(true);

            const response = await GeneratorService.renderHtml({
                stepData,
                generatedContent,
            });

            setPreviewHtml(response || "");
            setLoading(false);

            return response;
        } catch (error) {
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al renderizar preview");
            throw error;
        }
    }, [stepData, generatedContent, setLoading, setErrorMsg]);

    // Helper para disparar descarga de un blob
    const _triggerDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Descargar PDF (propuesta completa + resumen ejecutivo en paralelo)
    const downloadPdf = useCallback(async () => {
        try {
            setLoading(true);

            const name = stepData.step1?.clientCompany || stepData.step1?.clientName || "sin-nombre";

            // Ambas peticiones en paralelo; allSettled no cancela si una falla
            const [fullResult, execResult] = await Promise.allSettled([
                GeneratorService.renderPdf({ stepData, generatedContent }),
                GeneratorService.renderPdfExecutive({ stepData }),
            ]);

            let anySuccess = false;
            let anyError = false;

            if (fullResult.status === 'fulfilled') {
                _triggerDownload(fullResult.value, `propuesta-${name}.pdf`);
                anySuccess = true;
            } else {
                anyError = true;
            }

            if (execResult.status === 'fulfilled') {
                // Pequeño delay para evitar que el navegador bloquee la segunda descarga
                await new Promise(r => setTimeout(r, 300));
                _triggerDownload(execResult.value, `resumen-ejecutivo-${name}.pdf`);
                anySuccess = true;
            } else {
                anyError = true;
            }

            setLoading(false);

            if (anySuccess && !anyError) {
                setSuccessMsg("PDFs descargados exitosamente");
            } else if (anySuccess && anyError) {
                setSuccessMsg("Propuesta descargada. No se pudo generar el resumen ejecutivo.");
            } else {
                setErrorMsg("Error al descargar los PDFs");
            }
        } catch (error) {
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al descargar PDF");
            throw error;
        }
    }, [stepData, generatedContent, setLoading, setErrorMsg, setSuccessMsg]);

    const renderStepPreview = useCallback(async (stepId) => {
        try {
            setLoading(true);

            const stepNumber = Object.keys(STEP_ID_BY_NUMBER).find(
                (key) => STEP_ID_BY_NUMBER[key] === stepId
            );

            const html = await GeneratorService.renderHtml({
                stepData: {
                    step1: stepData.step1,
                    step2: stepData.step2,
                    step3: stepData.step3,
                    step4: stepData.step4,
                },
                stepsToRender: stepNumber ? [parseInt(stepNumber)] : null,
            });

            setPreviewHtmlByStep((prev) => ({
                ...prev,
                [stepId]: html,
            }));
            setLoading(false);
            return html;
        } catch (error) {
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al renderizar preview");
            throw error;
        }
    }, [stepData, setLoading, setErrorMsg]);

    const openPdfForStep = useCallback(async (stepId) => {
        try {
            setLoading(true);

            const stepNumber = Object.keys(STEP_ID_BY_NUMBER).find(
                (key) => STEP_ID_BY_NUMBER[key] === stepId
            );

            const blob = await GeneratorService.renderPdf({
                stepData: {
                    step1: stepData.step1,
                    step2: stepData.step2,
                    step3: stepData.step3,
                    step4: stepData.step4,
                },
                stepsToRender: stepNumber ? [parseInt(stepNumber)] : null,
            });

            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener,noreferrer");
            setLoading(false);
        } catch (error) {
            setLoading(false);
            const errorMessage = getErrorMessageFromError(error);
            setErrorMsg(errorMessage || "Error al generar PDF");
            throw error;
        }
    }, [stepData, setLoading, setErrorMsg]);

    // Cargar propuesta guardada desde BD
    const loadFromSavedProposal = useCallback(async (id) => {
        try {
            setLoading(true);
            const dto = await GeneratorService.getProposal(id);
            if (!dto) {
                setErrorMsg("Propuesta no encontrada");
                setLoading(false);
                return false;
            }
            setProposalId(dto.id);
            if (dto.rawText) setRawProposalText(dto.rawText);

            // Merge stepData from DB (steps 2-13) with initialStepData
            const merged = { ...initialStepData };
            if (dto.stepData) {
                for (const [key, value] of Object.entries(dto.stepData)) {
                    if (merged[key]) {
                        merged[key] = { ...merged[key], ...value };
                    } else {
                        merged[key] = value;
                    }
                }
            }

            // Step1 may not be in stepData (ProposalList polling only saves steps 2-13).
            // Always populate step1 from top-level proposal fields.
            merged.step1 = {
                ...merged.step1,
                clientName: dto.clientName || merged.step1.clientName || "",
                clientCompany: dto.clientCompany || merged.step1.clientCompany || "",
                country: dto.country || merged.step1.country || "Costa Rica",
                clientId: dto.clientId || merged.step1.clientId || null,
                fullProposalGenerated: dto.status === "COMPLETED" ? true : (merged.step1.fullProposalGenerated || false),
            };

            setStepData(merged);

            // Mark all steps as visited for a loaded proposal
            setVisitedSteps(new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]));
            if (dto.status === "COMPLETED") {
                setGenerationCompleted(true);
            }
            setLoading(false);
            return true;
        } catch (error) {
            setLoading(false);
            setErrorMsg("Error al cargar propuesta");
            return false;
        }
    }, [setLoading, setErrorMsg]);

    // Guardar propuesta actual a BD
    const saveProposalToDb = useCallback(async () => {
        try {
            setLoading(true);
            const payload = {
                clientId: stepData.step1?.clientId || null,
                clientName: stepData.step1?.clientName || "",
                clientCompany: stepData.step1?.clientCompany || "",
                projectName: stepData.step1?.clientCompany || stepData.step1?.clientName || "Propuesta",
                country: stepData.step1?.country || "",
                status: "COMPLETED",
                rawText: rawProposalText || "",
                stepData,
                pricingData: stepData.step13?.pricing || null,
                progress: 100,
                createdBy: authInfo?.id,
            };

            let result;
            if (proposalId) {
                result = await GeneratorService.updateProposal(proposalId, payload);
            } else {
                result = await GeneratorService.saveProposal(payload);
                setProposalId(result.id);
            }
            setSuccessMsg("Propuesta guardada exitosamente");
            setLoading(false);
            return result;
        } catch (error) {
            setLoading(false);
            setErrorMsg("Error al guardar propuesta");
            return null;
        }
    }, [stepData, rawProposalText, proposalId, authInfo, setLoading, setErrorMsg, setSuccessMsg]);

    const dismissGenerationCompleted = useCallback(() => {
        setGenerationCompleted(false);
    }, []);

    // Resetear todo el estado
    const resetGenerator = useCallback(() => {
        localStorage.removeItem(ACTIVE_JOB_KEY);
        setProposalId(null);
        setCurrentStep(1);
        setVisitedSteps(new Set([1]));
        setStepData(initialStepData);
        setGeneratedContent({
            markdown: null,
            html: null,
            sections: [],
        });
        setRawProposalText("");
        setIsGenerating(false);
        setGenerationProgress(0);
        setGenerationCompleted(false);
        setPricing(new PricingDto());
        setPreviewHtml("");
        setPreviewHtmlByStep({ cover: "", introduction: "", business_partners: "" });
    }, []);

    // Verificar si un paso está completo (solo si fue visitado explícitamente por el usuario)
    const isStepComplete = useCallback((step) => {
        if (!visitedSteps.has(step)) return false;
        const data = stepData[`step${step}`];
        if (!data) return false;

        switch (step) {
            case 1:
                return data.clientName && data.clientCompany && data.transcription;
            case 2:
                return data.coverTitle;
            case 3:
                return data.introduction;
            case 4:
                return true; // Paso solo visual (preview), basta con visitarlo
            case 5:
                return data.projectContext;
            case 6:
                return data.generalObjective;
            case 7:
                return data.specificObjectives?.length > 0;
            case 8:
                return data.scope;
            case 9:
                return data.architecture || data.architectureDiagram || data.technologies?.length > 0;
            case 10:
                return data.proposedSolution;
            case 11:
                return data.teamMembers?.length > 0;
            case 12:
                return data.timeline;
            case 13:
                return data.pricing?.total > 0;
            default:
                return false;
        }
    }, [stepData, visitedSteps]);

    return (
        <GeneratorDataContext.Provider
            value={{
                // Estado
                currentStep,
                stepData,
                generatedContent,
                rawProposalText,
                isGenerating,
                generationProgress,
                generationCompleted,
                pricing,
                previewHtml,
                previewHtmlByStep,

                // Acciones de navegación
                nextStep,
                prevStep,
                goToStep,
                setCurrentStep,

                // Acciones de datos
                updateStepData,
                setStepData,

                // Acciones de generación
                generateStepContent,
                generateFullProposal,
                generateRawProposal,
                regenerateSection,

                // Acciones de pricing
                calculatePricing,
                setPricing,

                // Acciones de preview
                renderPreview,
                downloadPdf,
                renderStepPreview,
                openPdfForStep,

                // BD persistence
                proposalId,
                setProposalId,
                loadFromSavedProposal,
                saveProposalToDb,

                // Utilidades
                resetGenerator,
                isStepComplete,
                dismissGenerationCompleted,
            }}
        >
            {children}
        </GeneratorDataContext.Provider>
    );
};

const useGeneratorData = () => {
    const context = useContext(GeneratorDataContext);
    return context || {};
};

export { GeneratorDataContext, GeneratorDataContextProvider, useGeneratorData };
